import { distinctUntilChanged, map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { animationFrameScheduler, from, fromEvent, merge, Observable, Subject, timer } from 'rxjs';

import {
  AfterViewInit,
  booleanAttribute,
  Directive,
  DoCheck,
  ElementRef,
  HostBinding,
  inject,
  Injectable,
  Input,
  OnDestroy,
} from '@angular/core';

import { LifecycleHooks } from '@shared/services/lifecycle-hooks.service';

@Injectable({
  providedIn: 'root',
})
export class TextareaAutoSizeService {
  private ctx = document.createElement('canvas').getContext('2d');

  private map = new Map<HTMLTextAreaElement, CSSStyleDeclaration | null>();

  register(input: HTMLTextAreaElement): void {
    this.map.set(input, null);
  }

  unregister(input: HTMLTextAreaElement): void {
    this.map.delete(input);
  }

  requestHeight(input: HTMLTextAreaElement, text: string, minRows: number): Observable<number> {
    return timer(0, animationFrameScheduler).pipe(
      map(() => {
        let style = this.map.get(input);

        if (!style) {
          style = window.getComputedStyle(input);
          this.map.set(input, style);
        }

        // twice because bug with font weight
        this.ctx.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
        this.ctx.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;

        let lineHeight = parseFloat(style.lineHeight) || 1.2;

        if (!style.lineHeight.endsWith('px')) {
          lineHeight = (lineHeight * parseFloat(style.fontSize)) / (style.lineHeight.endsWith('px') ? 100 : 1);
        }

        const width = parseFloat(style.width);
        const metrics: TextMetrics = this.ctx.measureText(text);
        const newLines = text?.split(/\r\n|\r|\n/).length || 1;

        return Math.max(minRows, Math.ceil(metrics.width / width) + newLines - 1) * lineHeight;
      }),
    );
  }
}

@Directive({
  selector: 'textarea[autoSize]',
  providers: [LifecycleHooks],
  standalone: false,
})
export class TextareaAutoSize implements DoCheck, AfterViewInit, OnDestroy {
  private el = inject<ElementRef<HTMLTextAreaElement>>(ElementRef);
  private lh = inject(LifecycleHooks);
  private ts = inject(TextareaAutoSizeService);

  private input: HTMLTextAreaElement = this.el.nativeElement;

  private check$ = new Subject<{ text: string; minRows: number }>();

  @Input({ alias: 'autoSize', transform: booleanAttribute })
  enabled?: boolean;

  @Input()
  autoSizeMinRows: number = 1;

  @HostBinding('style.overflow')
  readonly overflow = 'hidden';

  readonly fontsReady$ = from((document as any).fonts?.ready || Promise.resolve());

  private previousValue?: string;
  private previousMinRows?: number;

  constructor() {
    this.ts.register(this.input);
  }

  ngDoCheck(): void {
    const newValue = this.getValue();

    if (newValue !== this.previousValue || this.autoSizeMinRows !== this.previousMinRows) {
      this.check$.next({ text: newValue, minRows: this.autoSizeMinRows });
      this.previousValue = newValue;
      this.previousMinRows = this.autoSizeMinRows;
    }
  }

  ngAfterViewInit(): void {
    const parent = this.input?.closest('mat-form-field');

    if (parent instanceof HTMLElement) {
      parent.style.height = 'initial';
    }

    merge(this.fontsReady$, timer(1000).pipe(takeUntil(this.fontsReady$)))
      .pipe(
        switchMap(() =>
          merge(
            fromEvent(this.input, 'input').pipe(
              map(() => ({ text: this.getValue(), minRows: this.autoSizeMinRows })),
              startWith({ text: this.getValue(), minRows: this.autoSizeMinRows }),
            ),
            this.check$,
          ),
        ),
        distinctUntilChanged((a, b) => a.text === b.text && a.minRows === b.minRows),
        switchMap(({ text, minRows }) => this.ts.requestHeight(this.input, text, minRows)),
        distinctUntilChanged(),
        takeUntil(this.lh.destroy),
      )
      .subscribe((height: number) => (this.input.style.height = `${height + 2}px`));
  }

  ngOnDestroy(): void {
    this.ts.unregister(this.input);
  }

  private getValue(): string {
    return this.input.value || this.input.placeholder || '';
  }
}
